import React, { useState, useEffect } from "react";
import SuccessPopup from "../../myfin_v2/elements/SuccessPopup";
import ErrorPopup from "../../myfin_v2/elements/ErrorPopup";
import FSCss from "../../myfin_v2/css/overview.module.css";
import commoncss from "../../myfin_v2/css/common.module.css";
import { ReactComponent as GreenDot } from "../../myfin_v2/assets/icons/greenDotSmall.svg";
import { ReactComponent as BackArrow } from "../../myfin_v2/assets/icons/left_arrow.svg";
import { ReactComponent as EditIcon } from "../../assets/edit.svg";
import { ReactComponent as InfoIcon } from "../../myfin_v2/assets/icons/infoGrey.svg";
import {
  getEquityReclassificationApiHandler,
  saveEquityReclassificationApiHandler,
} from "../../WorkSpace/Components/ApiServices/apiHandler";
import { Spinner } from "react-bootstrap";

const formatAmount = (val) => {
  if (val === null || val === undefined || val === "") return "";

  const num = Number(val);
  if (Number.isNaN(num)) return "";

  if (num < 0) {
    return `(${Math.abs(num).toLocaleString("en-IN")})`;
  }

  return num.toLocaleString("en-IN");
};

const normalizeItem = (item, idx, prefix) => {
  const level2 =
    item?.levelTwo ??
    item?.level_two ??
    item?.level2 ??
    item?.Level2 ??
    "";

  const level3 =
    item?.levelThree ??
    item?.level_three ??
    item?.level3 ??
    item?.Level3 ??
    "";

  const closingBalance =
    item?.closingBalance !== undefined && item?.closingBalance !== null
      ? Number(item.closingBalance)
      : null;

  const updatedClosingBalance =
    item?.updatedClosingBalance !== undefined && item?.updatedClosingBalance !== null
      ? Number(item.updatedClosingBalance)
      : closingBalance;

  const reclassification =
    item?.reclassification !== undefined && item?.reclassification !== null
      ? Number(item.reclassification)
      : typeof closingBalance === "number" && typeof updatedClosingBalance === "number"
      ? updatedClosingBalance - closingBalance
      : 0;

  return {
    id: item?.groupingID || item?._id || item?.id || `${prefix}-${idx}`,
    groupingID: item?.groupingID || "",
    level2,
    level3,
    closingBalance,
    updatedClosingBalance,
    reclassification,
    rawItem: item,
  };
};

const ConfirmEquity = ({
  onClose,
  projectID,
  workspaceName = "",
  standaloneName = "",
}) => {
  const effectiveProjectId =
    projectID ||
    localStorage.getItem("project_id") ||
    sessionStorage.getItem("project_id");

  const [currentYearData, setCurrentYearData] = useState([]);
  const [previousYearData, setPreviousYearData] = useState([]);

  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");

  const [hoveredRowKey, setHoveredRowKey] = useState(null);

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errorData, setErrorData] = useState(null);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchEquityData = async (showLoading = true) => {
    if (!effectiveProjectId) {
      setLoading(false);
      return;
    }

    if (showLoading) setLoading(true);
    try {
      const res = await getEquityReclassificationApiHandler(effectiveProjectId);
      const payload = res?.data?.data || res?.data || {};

      const currentRows = Array.isArray(payload?.currentYear?.rows)
        ? payload.currentYear.rows
        : [];

      const previousRows = Array.isArray(payload?.previousYear?.rows)
        ? payload.previousYear.rows
        : [];

      setCurrentYearData(
        currentRows.map((item, index) => normalizeItem(item, index, "cy"))
      );

      setPreviousYearData(
        previousRows.map((item, index) => normalizeItem(item, index, "py"))
      );
    } catch (err) {
      console.log("Error fetching equity reclassification:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch equity reclassification";

      setErrorData({
        title: "Fetch Error",
        message: msg,
      });
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchEquityData();
  }, [effectiveProjectId]);

  const calculateTotal = (data, key) => {
    return data.reduce((acc, row) => {
      const val = row[key];
      return acc + (typeof val === "number" ? val : 0);
    }, 0);
  };

  const handleStartEdit = (section, id, val) => {
    setEditingCell(`${section}-${id}`);
    setEditValue(val !== null && val !== undefined ? String(val) : "");
  };

  const handleSaveEdit = (section, id) => {
    const rawVal = editValue.replace(/,/g, "").trim();
    let numVal;

    if (rawVal === "") {
      numVal = null;
    } else if (rawVal.startsWith("(") && rawVal.endsWith(")")) {
      numVal = -Number(rawVal.slice(1, -1).trim());
    } else {
      numVal = Number(rawVal);
    }

    const updateRows = (prevRows) =>
      prevRows.map((row) => {
        if (row.id === id) {
          const updated = Number.isNaN(numVal)
            ? row.updatedClosingBalance
            : numVal;

          return {
            ...row,
            updatedClosingBalance: updated,
            isEdited: true,
          };
        }
        return row;
      });

    if (section === "cy") {
      setCurrentYearData(updateRows);
    } else {
      setPreviousYearData(updateRows);
    }

    setEditingCell(null);
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
  };

  const buildYearPayload = (yearData) => {
    const filtered = yearData.filter((row) => {
      if (row.isEdited) {
        return true;
      }
      if (
        row.reclassification !== 0 &&
        row.reclassification !== null &&
        row.reclassification !== undefined
      ) {
        return true;
      }
      return false;
    });

    const targetRows = filtered.length > 0 ? filtered : yearData;

    return targetRows.map((row) => ({
      levelTwo: row.level2,
      levelThree: row.level3,
      updatedClosingBalance:
        row.updatedClosingBalance === null ||
        row.updatedClosingBalance === undefined
          ? null
          : Number(row.updatedClosingBalance),
    }));
  };

  const handleSave = async () => {
    const body = {
      currentYear: buildYearPayload(currentYearData),
      previousYear: buildYearPayload(previousYearData),
    };

    setSaving(true);

    try {
      await saveEquityReclassificationApiHandler(effectiveProjectId, body);
      await fetchEquityData(false);
      setShowSuccessPopup(true);
    } catch (err) {
      console.log("Error saving equity reclassification:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save equity changes";

      setErrorData({
        title: "Save Error",
        message: msg,
      });
    } finally {
      setSaving(false);
    }
  };

  const thStyle = (align = "left") => ({
    padding: "12px 20px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#205a46",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    textAlign: align,
    lineHeight: "1.4",
    verticalAlign: "bottom",
    backgroundColor: "#f4faf5",
    position: "sticky",
    top: 0,
    zIndex: 2,
    borderTop: "1px solid #E2E8E5",
    borderBottom: "1px solid #E2E8E5",
  });

  const renderTableSection = (title, data, sectionKey) => {
    const totalClosing = calculateTotal(data, "closingBalance");
    const totalUpdated = calculateTotal(data, "updatedClosingBalance");
    const totalReclassification = calculateTotal(data, "reclassification");
    const difference = totalUpdated - totalClosing;
    const isBalanced = Math.abs(difference) < 0.01;

    return (
      <div
        style={{
          background: "#ffffff",
          borderRadius: "10px",
          border: "1px solid #EAEEED",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
          display: "flex",
          flexDirection: "column",
          height: "640px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "15px 20px 13px 20px",
            fontSize: "15px",
            fontWeight: "700",
            color: "#183930",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            flexShrink: 0,
            backgroundColor: "#ffffff",
          }}
        >
          {title}
        </div>

        <div
          className="confirm-equity-scroll"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              tableLayout: "fixed",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f4faf5" }}>
                <th style={{ ...thStyle("left"), width: "24%" }}>
                  LEVEL 2 ITEMS
                </th>
                <th style={{ ...thStyle("left"), width: "24%" }}>
                  LEVEL 3 ITEMS
                </th>
                <th style={{ ...thStyle("right"), width: "22%" }}>
                  CLOSING BALANCE AS PER
                  <br />
                  MAPPING & TRANSLATION
                </th>
                <th style={{ ...thStyle("right"), width: "16%" }}>
                  UPDATED
                  <br />
                  CLOSING BALANCE
                </th>
                <th style={{ ...thStyle("right"), width: "14%" }}>
                  RECLASSIFICATION
                </th>
              </tr>
            </thead>

            <tbody>
              {data.map((row) => {
                const rowKey = `${sectionKey}-${row.id}`;
                const isEditing = editingCell === rowKey;
                const isHovered = hoveredRowKey === rowKey;

                return (
                  <tr
                    key={row.id}
                    style={{
                      backgroundColor: "#ffffff",
                      transition: "background-color 0.15s ease",
                    }}
                    onMouseEnter={() => setHoveredRowKey(rowKey)}
                    onMouseLeave={() => setHoveredRowKey(null)}
                  >
                    <td
                      style={{
                        padding: "13px 20px",
                        fontSize: "14px",
                        color: "#3e3d3d",
                        textAlign: "left",
                        borderBottom: "1px solid #f3f5f5"
                      }}
                    >
                      {row.level2}
                    </td>

                    <td
                      style={{
                        padding: "13px 20px",
                        fontSize: "14px",
                        color: "#3e3d3d",
                        textAlign: "left",
                        borderBottom: "1px solid #f3f5f5"
                      }}
                    >
                      {row.level3}
                    </td>

                    <td
                      style={{
                        padding: "13px 20px",
                        fontSize: "13px",
                        color: "#3e3d3d",
                        textAlign: "right",
                        borderBottom: "1px solid #f3f5f5"
                      }}
                    >
                      {formatAmount(row.closingBalance)}
                    </td>

                    <td
                      style={{
                        padding: "13px 20px",
                        fontSize: "14px",
                        color: "#3e3d3d",
                        textAlign: "right",
                        position: "relative",
                        borderBottom: "1px solid #f3f5f5"
                      }}
                    >
                      {isEditing ? (
                        <input
                          type="text"
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleSaveEdit(sectionKey, row.id);
                            }
                            if (e.key === "Escape") {
                              handleCancelEdit();
                            }
                          }}
                          onBlur={() => handleSaveEdit(sectionKey, row.id)}
                          style={{
                            width: "115px",
                            padding: "3px 6px",
                            fontSize: "13px",
                            textAlign: "right",
                            borderRadius: "4px",
                            border: "1px solid #104E43",
                            outline: "none",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            display: "inline-block",
                            position: "relative",
                          }}
                        >
                          <span>
                            {row.updatedClosingBalance !== null &&
                            row.updatedClosingBalance !== undefined
                              ? formatAmount(row.updatedClosingBalance)
                              : ""}
                          </span>

                          <span
                            style={{
                              position: "absolute",
                              left: "100%",
                              top: "50%",
                              transform: "translateY(-50%)",
                              marginLeft: "6px",
                              display: "inline-flex",
                              alignItems: "center",
                              cursor: "pointer",
                              opacity: isHovered ? 1 : 0,
                              visibility: isHovered ? "visible" : "hidden",
                              transition:
                                "opacity 0.15s ease, visibility 0.15s ease",
                            }}
                            title="Edit updated closing balance"
                            onClick={() =>
                              handleStartEdit(
                                sectionKey,
                                row.id,
                                row.updatedClosingBalance
                              )
                            }
                          >
                            <EditIcon width={17} height={17} />
                          </span>
                        </div>
                      )}
                    </td>

                    <td
                      style={{
                        padding: "13px 20px",
                        fontSize: "14px",
                        color: "#3e3d3d",
                        textAlign: "right",
                        borderBottom: "1px solid #f3f5f5"
                      }}
                    >
                      {formatAmount(row.reclassification)}
                    </td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot
              style={{
                position: "sticky",
                bottom: 0,
                zIndex: 2,
                backgroundColor: "#ffffff",
              }}
            >
              <tr
                className={commoncss.fw700}
                style={{
                  backgroundColor: "#ffffff",
                  fontWeight: "700",
                }}
              >
                <td
                  className={commoncss.fw700}
                  style={{
                    padding: "14px 20px",
                    fontSize: "13.5px",
                    fontWeight: "700",
                    color: "#111111",
                    textAlign: "left",
                    backgroundColor: "#ffffff",
                    borderTop: "1px solid #E2E8E5",
                  }}
                >
                  <b>TOTAL</b>
                </td>

                <td
                  style={{
                    padding: "14px 20px",
                    backgroundColor: "#ffffff",
                    borderTop: "1px solid #E2E8E5",
                  }}
                />

                <td
                  className={commoncss.fw700}
                  style={{
                    padding: "14px 20px",
                    fontSize: "13.5px",
                    fontWeight: "700",
                    color: "#111111",
                    textAlign: "right",
                    backgroundColor: "#ffffff",
                    borderTop: "1px solid #E2E8E5",
                  }}
                >
                  <b>{formatAmount(totalClosing)}</b>
                </td>

                <td
                  className={commoncss.fw700}
                  style={{
                    padding: "14px 20px",
                    fontSize: "13.5px",
                    fontWeight: "700",
                    color: isBalanced ? "#111111" : "#B76E00",
                    textAlign: "right",
                    backgroundColor: "#ffffff",
                    borderTop: "1px solid #E2E8E5",
                  }}
                >
                  <b>{formatAmount(totalUpdated)}</b>
                </td>

                <td
                  style={{
                    padding: "14px 20px",
                    textAlign: "right",
                    fontSize: "13.5px",
                    fontWeight: "700",
                    color: isBalanced ? "#111111" : "#B76E00",
                    backgroundColor: "#ffffff",
                    borderTop: "1px solid #E2E8E5",
                  }}
                >
                  <b>{formatAmount(totalReclassification)}</b>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          .confirm-equity-scroll::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          .confirm-equity-scroll {
            -ms-overflow-style: none !important;
            scrollbar-width: none !important;
          }
        `}
      </style>
      <ErrorPopup
        isOpen={errorData !== null}
        title={errorData?.title || "Error"}
        message={errorData?.message}
        onClose={() => setErrorData(null)}
      />

      <SuccessPopup
        isOpen={showSuccessPopup}
        message="Equity changes saved successfully"
        onClose={() => setShowSuccessPopup(false)}
      />

      <div
        style={{
          padding: "12px 14px 30px 14px",
          minHeight: "100%",
          backgroundColor: "#f8f9f9",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: "15px",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0",
          }}
        >
          <div
            className={FSCss.headerLeftContainer}
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <div
              className={FSCss.iconContainer}
              style={{
                width: "auto",
                paddingRight: "15px",
                paddingLeft: "0",
                display: "flex",
                alignItems: "center",
              }}
            >
              <BackArrow
                className="clickable"
                style={{
                  cursor: "pointer",
                  marginTop: "5px",
                  width: "14px",
                  height: "14px",
                }}
                onClick={onClose}
              />
            </div>

            <div
              className={FSCss.topBarHeading}
              style={{
                alignContent: "center",
                cursor: "pointer",
                fontWeight: "500",
                fontSize: "14px",
              }}
              onClick={onClose}
            >
              {workspaceName}
            </div>

            <div className={FSCss.iconDotContainer}>
              <GreenDot />
            </div>

            <div
              className={FSCss.topBarHeading}
              style={{
                alignContent: "center",
                cursor: "pointer",
                fontWeight: "500",
                fontSize: "14px",
              }}
              onClick={onClose}
            >
              {standaloneName}
            </div>

            <div className={FSCss.iconDotContainer}>
              <GreenDot />
            </div>

            <div
              className={commoncss.headerLable}
              style={{
                alignContent: "center",
                fontWeight: "500",
                fontSize: "14px",
                color: "#111111",
              }}
            >
              Confirm Equity
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving || loading}
            style={{
              backgroundColor: "#205a46",
              color: "#ffffff",
              border: "none",
              borderRadius: "8px",
              padding: "8px 20px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: saving || loading ? "not-allowed" : "pointer",
              opacity: saving || loading ? 0.7 : 1,
              boxShadow: "0 1px 2px rgba(16, 78, 67, 0.1)",
              transition: "background-color 0.15s ease",
            }}
            onMouseEnter={(e) => {
              if (!saving && !loading) {
                e.currentTarget.style.backgroundColor = "#0d3f36";
              }
            }}
            onMouseLeave={(e) => {
              if (!saving && !loading) {
                e.currentTarget.style.backgroundColor = "#104E43";
              }
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        <div
          style={{
            backgroundColor: "#F4faf5",
            border: "1px solid #c2e6d1",
            borderRadius: "8px",
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <InfoIcon width={17} height={17} style={{ flexShrink: 0 }} />
          <div style={{ fontSize: "15px", color: "#205a46", lineHeight: "1.4", fontWeight: "500" }}>
            Updated column to add back to the same total of other equity
          </div>
        </div>

        {loading ? (
          <div
            style={{
              height: "60vh",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "14px",
              }}
            >
              {/* Center spinner at top */}
              <Spinner
                animation="grow"
                size="sm"
                style={{
                  width: "18px",
                  height: "18px",
                  color: "#205a46",
                  animationDelay: "0s",
                }}
              />
              {/* Left and right spinners at bottom moved to the sides */}
              <div style={{ display: "flex", gap: "48px" }}>
                <Spinner
                  animation="grow"
                  size="sm"
                  style={{
                    width: "18px",
                    height: "18px",
                    color: "#205a46",
                    animationDelay: "0s",
                  }}
                />
                <Spinner
                  animation="grow"
                  size="sm"
                  style={{
                    width: "18px",
                    height: "18px",
                    color: "#205a46",
                    animationDelay: "0s",
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            {renderTableSection(
              "CURRENT YEAR",
              currentYearData,
              "cy"
            )}

            {renderTableSection(
              "PREVIOUS YEAR",
              previousYearData,
              "py"
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ConfirmEquity;